import { useSaveImpactsOnStakeholders } from '@Domains/ImpactsOnStakeholder';
import { useDisclosure } from '@hooks/useDisclosure';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Button, Snackbar } from '@mui/material';
import { mapImpactsOnStakeholdersToFormValues } from '@Router/ImpactsOnStakeholdersManagement/utils';

import { findUserRoles } from '@Utils/roleUtils';
import { updateCompletedSection } from '@Utils/updateCompletedSection';
import { useParams } from 'react-router-dom';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { useImpactsOnStakeholdersForm } from '../ImpactsOnStakeholdersFormProvider';
import { mapToStakeholderPayload } from './utils';
import {
  deleteAssets,
  IUploadImage,
  useDeleteImage,
} from '@Domains/UploadImage';

const ImpactsOnStakeholderSaveButton = () => {
  const { getValues, reset, trigger, setValue } =
    useImpactsOnStakeholdersForm();
  const { id = '' } = useParams();
  const [{ loading, error }, saveImpactsOnStakeholder] =
    useSaveImpactsOnStakeholders();
  const { refetchProgressWithUpdate, isPublished } = useProgressBar();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();
  const { user } = useProfile();

  const { deleteImage } = useDeleteImage(Number(id));

  const handleSave = async () => {
    trigger();
    if (user) {
      const currentFormValues = getValues();
      const { cvupCreatorRole, cvupApproverRole } = findUserRoles(user.N_ROLES);

      setValue(
        'businessValueChain.completedSection',
        updateCompletedSection(
          currentFormValues.businessValueChain?.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );
      setValue(
        'stakeholderAnalysis.completedSection',
        updateCompletedSection(
          currentFormValues.stakeholderAnalysis?.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );

      const businessValueChainAssetsToDelete =
        currentFormValues.businessValueChain?.assets?.filter(
          (asset: IUploadImage) => asset.markedForDeletion
        );

      const stakeholderAnalysisAssetsToDelete =
        currentFormValues.stakeholderAnalysis?.assets?.filter(
          (asset: IUploadImage) => asset.markedForDeletion
        );

      if (businessValueChainAssetsToDelete?.length) {
        deleteAssets(
          'businessValueChain',
          businessValueChainAssetsToDelete,
          deleteImage
        );
      }

      if (stakeholderAnalysisAssetsToDelete?.length) {
        deleteAssets(
          'stakeholderAnalysis',
          stakeholderAnalysisAssetsToDelete,
          deleteImage
        );
      }

      const updatedFormValues = getValues();
      const payload = mapToStakeholderPayload(updatedFormValues);

      try {
        const response = await saveImpactsOnStakeholder({
          data: { ...payload },
          params: { reportId: id },
        });

        if (response) {
          const dataFormValues = mapImpactsOnStakeholdersToFormValues(
            response.data
          );
          reset(
            { ...dataFormValues },
            { keepErrors: true, keepIsValidating: false }
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        refetchProgressWithUpdate(true);
        openSnackbar();
      }
    }
  };

  return (
    <>
      <Button
        type="submit"
        color="inherit"
        startIcon={<SaveIcon />}
        onClick={handleSave}
        loading={loading}
        disabled={isPublished}
      >
        บันทึก
      </Button>
      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!error ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {!!error ? 'เกิดข้อผิดพลาดในการบันทึก' : 'บันทึกสําเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImpactsOnStakeholderSaveButton;
